// src/components/Inquiry/items/SpecCell.tsx
//
// The spec of one item line, read-only. A spec runs to several pipe-separated
// clauses and the buyer who raised the line already knows them - what the row
// is for is the quantity and the state. So the spec shows as a single
// ellipsized line in the table, and the full text is a click away: the cell
// expands in place (not a tooltip, which cannot be selected) and collapses
// again.
//
// Shared by ProductLineRow and OtherLineRow, which both draw their spec as a
// stack of two lines - the first the material/kind, the second the
// dimensions or description.
import { FunctionComponent, ReactNode, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import { CELL_PRIMARY, CELL_SECONDARY } from "components/Dashboard/dashboardTable";

type PropsType = {
  /** One line per element of the spec, top to bottom. */
  lines: ReactNode[];
};

const lineStyle = {
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

const SpecCell: FunctionComponent<PropsType> = ({ lines }) => {
  const [expanded, setExpanded] = useState(false);
  // A single-line spec has nothing to unfold - leave it as it is.
  const singleLine = lines.length <= 1;

  return (
    <Tooltip
      title={singleLine ? "" : (expanded ? "Collapse" : "Show full spec")}
      placement="top"
    >
      <span
        style={{ display: "block", cursor: singleLine ? "default" : "pointer" }}
        onClick={() => !singleLine && setExpanded((v) => !v)}
      >
        {lines.map((line, index) => (
          <Typography
            key={index}
            sx={{
              ...(index === 0 ? CELL_PRIMARY : CELL_SECONDARY),
              ...(expanded ? { whiteSpace: "pre-wrap" } : lineStyle),
            }}
          >
            {line}
          </Typography>
        ))}
      </span>
    </Tooltip>
  );
};

export default SpecCell;
